import React, { useState, useEffect, useRef } from "react";
import axios, { API } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { PaymentModal } from "./modals/PaymentModal";
import { AiEngineSection } from "./settings/AiEngineSection";
import { PremiumSuccessPopup } from "./modals/PremiumSuccessPopup";
import { ThemePickerModal } from "./modals/ThemePickerModal";
import { ProTeaserModal } from "./modals/ProTeaserModal";
import { BetaAccessModal } from "./modals/BetaAccessModal";
import { MoodJournalModal } from "./modals/MoodJournalModal";
import { NotificationsModal } from "./modals/NotificationsModal";
import { VoicePickerModal } from "./modals/VoicePickerModal";
import { StoriesFeedModal } from "./feed/StoriesFeedModal";
import { TrialExpiredOverlay } from "./TrialExpiredOverlay";
import { VoiceCallModal } from "./voice/VoiceCallModal";
import { startRecording, playBase64Mp3 } from "../voiceUtils";
import { DailyCheckinCard } from "./DailyCheckinCard";
import { CameraCaptureModal } from "./vision/CameraCaptureModal";
import { PhotoPickerModal } from "./vision/PhotoPickerModal";
import { VisionSourcePicker } from "./vision/VisionSourcePicker";
import { PeopleModal } from "./modals/PeopleModal";
import { IntegrationsModal } from "./modals/IntegrationsModal";
import { StreakBadge } from "./StreakBadge";
import { EmailVerifyBanner } from "./EmailVerifyBanner";
import { BirthdaysCard } from "./BirthdaysCard";
import { PromoRedeemModal } from "./modals/PromoRedeemModal";
import { AdminPromosModal } from "./modals/AdminPromosModal";
import { OutboundCallsModal } from "./modals/OutboundCallsModal";
import { CreatorStudioModal } from "./modals/CreatorStudioModal";
import { ShareNudgePill } from "./ShareNudgePill";
import { AdminEmailHealthModal } from "./modals/AdminEmailHealthModal";
import { AdminBillingPortalModal } from "./modals/AdminBillingPortalModal";
import { WallpaperGalleryModal, downloadWallpaper } from "./modals/WallpaperGalleryModal";
import { ReferralModal } from "./modals/ReferralModal";
import { SocialConnectPrompt } from "./modals/SocialConnectPrompt";
import { DeleteAccountModal } from "./modals/DeleteAccountModal";
import { AgeGateModal } from "./modals/AgeGateModal";
import { ParentalSetupModal, ParentalUnlockModal } from "./modals/ParentalControlsModals";
import { CrisisCard } from "./CrisisCard";
import { SendTextConfirmCard } from "./SendTextConfirmCard";
import { SnapShareConfirmCard } from "./SnapShareConfirmCard";
import { TikTokShareConfirmCard } from "./TikTokShareConfirmCard";
import { InstagramShareConfirmCard } from "./InstagramShareConfirmCard";
import { CaptureMomentPill } from "./CaptureMomentPill";
import { SecretLockBadge } from "./SecretLockBadge";
import { ARCameraModal } from "./vision/ARCameraModal";
import { PhotoShareSheet } from "./PhotoShareSheet";
import { BetaIcon } from "./BetaIcon";

// Linkify URLs in chat content so Aura's "here's the link" actually becomes
// a tappable link. Splits text on URL boundaries and renders <a> for each match.
// Trailing punctuation (., ,, !, ?, ), ]) is stripped from the URL and re-appended
// as plain text so sentences like "check it: https://x.com/abc." stay clean.
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
// Phrases that suggest the user is about to describe something visual and
// would benefit from attaching a photo. Match is fuzzy — we only need to be
// confident enough to offer the nudge, not certain.
//
// Tuning notes:
// - Use word boundaries so "viewing" doesn't trigger "view"
// - Require a present-tense visual cue OR an explicit pointer ("this", "here")
//   so we don't fire on memory ("I saw a movie yesterday")
// - Don't trigger if user already typed about an attached image
const SMART_PHOTO_HINTS = [
  /\b(look|peep|check|peek) at (this|that|these|those|my|the)\b/i,
  /\b(look at|see) this\s+(view|sky|sunset|sunrise|cloud|moon|stars|mountain|tree|flower|dog|cat|pet|outfit|fit|hair|drink|coffee|food|meal|plate|setup|room|place|night|morning|view)\b/i,
  /\b(check|peep) (this|that)\s+(out|view)?\b/i,
  /\b(wanna|want to|wanna|gotta) see (this|that)\b/i,
  /\b(you should see|you have to see|i have to show you|i wish you could see)\b/i,
  /\b(this|that|my) (sky|sunset|sunrise|view|moon|stars|outfit|fit|hair|food|meal|coffee|drink|setup|room)\s+(is|looks)\b/i,
  /\bmy view (right now|today|tonight)\b/i,
  /\b(here'?s|here is) (what|the) (it|view|sky|sunset)\b/i,
  /\b(snap|shot|pic) of (this|that|my)\b/i,
  /\bgolden hour\b/i,
];

function shouldSuggestPhoto(text) {
  if (!text || text.length < 5) return false;
  // Skip if they've already mentioned a photo/picture explicitly — they
  // probably already know about the camera button.
  if (/\b(photo|picture|pic|snap|shot|image)\b.*\b(attached|here|sent|i sent)\b/i.test(text)) return false;
  return SMART_PHOTO_HINTS.some(re => re.test(text));
}

// Phase 22.1 — AR lens preview clips, one per Aura mood. CDN-hosted MP4s so
// the app bundle stays light. Played silently in the mood tooltip when the
// user taps Aura's mood emoji — they see "what Aura looks like right now"
// via her active Snap AR lens. Updating these URLs requires no redeploy.
const MOOD_LENS_PREVIEWS = {
  happy:         "https://customer-assets.emergentagent.com/job_idea-recovery-2/artifacts/eh4x7wpu_happy%20%281%29.mp4",
  thoughtful:    "https://customer-assets.emergentagent.com/job_idea-recovery-2/artifacts/9x4wuaze_thoughtful%20%281%29.mp4",
  calm:          "https://customer-assets.emergentagent.com/job_idea-recovery-2/artifacts/cfgrmihb_calm.mp4",
  energetic:     "https://customer-assets.emergentagent.com/job_idea-recovery-2/artifacts/7qsjg8uy_energetic.mp4",
  melancholic:   "https://customer-assets.emergentagent.com/job_idea-recovery-2/artifacts/3v4b970h_melancholic.mp4",
  curious:       "https://customer-assets.emergentagent.com/job_idea-recovery-2/artifacts/icxk4dac_Cosmic%20Cartographer.mp4",
  playful:       "https://customer-assets.emergentagent.com/job_idea-recovery-2/artifacts/3y8kbws4_playful%20%282%29.mp4",
  contemplative: "https://customer-assets.emergentagent.com/job_idea-recovery-2/artifacts/aidhc6l1_moonlight%20halo.mp4",
};

// Optimistic acknowledgement for slow chat intents.
// Aura's responses for playlist creation, tweet drafting, mood switches, etc.
// can take 4-8 seconds (LLM call + external API call). Without feedback the UI
// feels frozen. We immediately drop a tiny ack bubble in chat that gets
// replaced by Aura's real reply when it arrives.
//
// Returns one of: { ack: string } | null
// Different intents get different ack copy so it feels intentional, not canned.
const SLOW_INTENT_ACKS = [
  { re: /\b(remake|regenerate|do another|do it again|try again|one more)\b.*\b(playlist|that)\b/i, ack: "✨ on it — building you a fresh one..." },
  { re: /\b(make|build|create|curate|put together)\b.*\b(playlist|songs|tracks)\b/i, ack: "✨ sure — give me a sec to pull this together..." },
  { re: /\bspotify playlist\b/i, ack: "✨ sure — give me a sec to pull this together..." },
  { re: /\b(post|tweet|share|send) (this|that) (to )?(x|twitter)\b/i, ack: "🐦 drafting that up for you..." },
  { re: /\b(tweet this|send to x)\b/i, ack: "🐦 drafting that up for you..." },
  { re: /\b(switch|change|set|flip|put|make) (me|aura|it|wallpaper|my wallpaper|the wallpaper|my mood)\b.*\b(to|into)\b/i, ack: "🎨 switching the vibe..." },
  { re: /\b(remember|add) (my|that)\s+[a-z]+\s+[A-Z]/i, ack: "💜 noted — adding them to your circle..." },
];

function detectSlowAck(text) {
  if (!text) return null;
  for (const { re, ack } of SLOW_INTENT_ACKS) {
    if (re.test(text)) return ack;
  }
  return null;
}

function renderMessageContent(text) {
  if (!text) return text;
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) => {
    if (!part) return null;
    if (URL_REGEX.test(part)) {
      // reset regex state (it's stateful with /g)
      URL_REGEX.lastIndex = 0;
      // peel trailing punctuation back into surrounding text
      const tailMatch = part.match(/([.,!?:;)\]]+)$/);
      const tail = tailMatch ? tailMatch[1] : "";
      const url = tail ? part.slice(0, -tail.length) : part;
      const href = url.startsWith("http") ? url : `https://${url}`;
      return (
        <span key={i}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="chat-link"
            data-testid="chat-message-link"
          >
            {url}
          </a>
          {tail}
        </span>
      );
    }
    URL_REGEX.lastIndex = 0;
    return <span key={i}>{part}</span>;
  });
}

// Tweet confirmation card — renders inline below Aura's reply whenever she
// detects a post_tweet intent. User can edit the text in place, confirm to
// actually post, or cancel. Keeps the destructive action behind a deliberate
// click rather than letting Aura post automatically.
function TweetConfirmCard({ pending, onSent, onCancelled }) {
  const [text, setText] = React.useState(pending?.tweet_text || "");
  const [posting, setPosting] = React.useState(false);
  const [status, setStatus] = React.useState(null); // 'sent' | 'cancelled' | 'error' | null
  const [errorMsg, setErrorMsg] = React.useState("");
  const [postedUrl, setPostedUrl] = React.useState(null);
  const [postedUsername, setPostedUsername] = React.useState(null);
  const [mediaWarn, setMediaWarn] = React.useState(null);
  // Phase 29.F/29.H — include the user's photo or wallpaper as a previewed
  // image attachment. Newer chat responses carry attachment_kind/attachment_id;
  // older builds still send wallpaper_id only (treated as a wallpaper).
  const attachmentKind = pending?.attachment_kind || (pending?.wallpaper_id ? "wallpaper" : null);
  const attachmentId = pending?.attachment_id || pending?.wallpaper_id || null;
  const previewSrc = attachmentId && attachmentKind === "user_photo"
    ? `${API}/photos/${attachmentId}/image`
    : attachmentId && attachmentKind === "wallpaper"
      ? `${API}/wallpaper/${attachmentId}/image`
      : null;

  const charCount = text.length;
  const overLimit = charCount > 280;

  if (status === "sent") {
    return (
      <div className="tweet-confirm-card sent" data-testid="tweet-confirm-card-sent">
        <span>
          ✓ Posted to X
          {postedUsername ? <> as <strong>@{postedUsername}</strong></> : null}
          {mediaWarn ? " (text only — reconnect X to attach images)" : ""}
        </span>
        {postedUrl && (
          <a href={postedUrl} target="_blank" rel="noopener noreferrer" className="chat-link" data-testid="tweet-confirm-view-link">
            View tweet
          </a>
        )}
      </div>
    );
  }
  if (status === "cancelled") {
    return (
      <div className="tweet-confirm-card cancelled" data-testid="tweet-confirm-card-cancelled">
        ↩ Tweet cancelled — nothing posted.
      </div>
    );
  }

  return (
    <div className="tweet-confirm-card" data-testid="tweet-confirm-card">
      <div className="tweet-confirm-header">
        <span className="tweet-confirm-bird">𝕏</span>
        <span className="tweet-confirm-label">Ready to post</span>
        <span className={`tweet-confirm-count ${overLimit ? "over" : ""}`}>
          {charCount}/280
        </span>
      </div>
      <textarea
        className="tweet-confirm-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={400}
        data-testid="tweet-confirm-text"
      />
      {previewSrc && (
        <div className="tweet-confirm-media" data-testid="tweet-confirm-media-preview">
          <img src={previewSrc} alt="Will attach to tweet" />
          <span className="tweet-confirm-media-label">
            📎 {attachmentKind === "user_photo" ? "your photo" : "wallpaper"}
          </span>
        </div>
      )}
      {errorMsg && <div className="tweet-confirm-error">{errorMsg}</div>}
      <div className="tweet-confirm-actions">
        <button
          className="tweet-confirm-cancel"
          onClick={() => { setStatus("cancelled"); onCancelled?.(); }}
          disabled={posting}
          data-testid="tweet-confirm-cancel"
        >
          Cancel
        </button>
        <button
          className="tweet-confirm-send"
          disabled={posting || overLimit || !text.trim()}
          data-testid="tweet-confirm-send"
          onClick={async () => {
            setPosting(true);
            setErrorMsg("");
            try {
              const r = await axios.post(`${API}/x/tweet`, {
                text,
                attachment_kind: attachmentKind,
                attachment_id: attachmentId,
              });
              const tweet = r.data?.tweet || {};
              const tid = r.data?.tweet_id || tweet.id || tweet.tweet_id || r.data?.id;
              // Phase 30.X.K — prefer the canonical URL (with @username) the
              // backend builds. Falls back to the generic /i/web/status/ path
              // for older backends that don't surface it. The canonical
              // form is critical: it lets the user instantly verify the
              // tweet went to their account, not some phantom one.
              const url = r.data?.canonical_url
                || (tid ? `https://x.com/i/web/status/${tid}` : null);
              setPostedUrl(url);
              setPostedUsername(r.data?.x_username || null);
              // Warn if image attach failed but tweet went text-only
              if (attachmentId && r.data?.media_attached === false) {
                setMediaWarn(r.data?.media_error || "upload_failed");
              }
              setStatus("sent");
              onSent?.(url);
            } catch (e) {
              setErrorMsg(e?.response?.data?.detail || "Couldn't post — try again?");
            } finally {
              setPosting(false);
            }
          }}
        >
          {posting ? "Posting…" : (previewSrc ? "Send to X with photo" : "Send to X")}
        </button>
      </div>
    </div>
  );
}

export function MainApp() {
  const { user, refreshUser, logout } = useAuth();
  const [wallpaper, setWallpaper] = useState(null);
  const [wallpaperId, setWallpaperId] = useState(null);
  const [mood, setMood] = useState({ mood: "calm", emoji: "😌", description: "Peaceful and serene" });
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  // Phase 30.X.J.2 — "still thinking..." / "this one's a longer thought..."
  // whispers that fade in next to the Newton's-cradle dots after Aura has
  // been computing for a noticeable while. Makes long replies feel
  // intentional ("quality takes time") instead of broken/frozen.
  //   phase 0 → just dots, <8s elapsed
  //   phase 1 → "still thinking..." + dots, ≥8s
  //   phase 2 → "this one's a longer thought..." + dots, ≥15s
  const [thinkingPhase, setThinkingPhase] = useState(0);
  useEffect(() => {
    if (!isSending) { setThinkingPhase(0); return; }
    const t1 = setTimeout(() => setThinkingPhase(1), 8000);
    const t2 = setTimeout(() => setThinkingPhase(2), 15000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isSending]);
  const [isGeneratingWallpaper, setIsGeneratingWallpaper] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [newPrompt, setNewPrompt] = useState("");
  const [initialPrompt, setInitialPrompt] = useState("");
  const [showPayment, setShowPayment] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMoodJournal, setShowMoodJournal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showProTeaser, setShowProTeaser] = useState(false);  // Phase 25.1 — Aura Pro waitlist
  const [showBetaAccess, setShowBetaAccess] = useState(false);  // Phase 25.4 — Snap + Spotify beta request
  const [currentTheme, setCurrentTheme] = useState('aura-soft');
  const [showStories, setShowStories] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Phase 30 — Beta UI overhaul: master theme (light/dark/neutral).
  // Persisted in localStorage; applied as `data-beta-theme` on <html>.
  // This is independent of the existing `data-theme` color picker and
  // the `data-dynamic-mood` overlay — those still work on top.
  const [betaTheme, setBetaTheme] = useState(() => {
    try { return localStorage.getItem("aura_beta_theme") || "dark"; } catch { return "dark"; }
  });
  useEffect(() => {
    try {
      document.documentElement.setAttribute("data-beta-theme", betaTheme);
      localStorage.setItem("aura_beta_theme", betaTheme);
    } catch {}
  }, [betaTheme]);

  // Phase 30.B — drag-to-expand composer (touch + pointer)
  const [composerExpanded, setComposerExpanded] = useState(false);
  const dragStartYRef = useRef(null);

  // Phase 30.B — in-chat wallpaper share sheet
  const [showWallpaperShare, setShowWallpaperShare] = useState(false);

  // Phase 30.G — Onboarding theme picker (shown BEFORE first wallpaper prompt)
  const [onboardingThemeDone, setOnboardingThemeDone] = useState(false);
  const [onboardingThemeGroup, setOnboardingThemeGroup] = useState(null);

  // Voice state
  const [preferredVoice, setPreferredVoice] = useState('nova');
  const [showVoicePicker, setShowVoicePicker] = useState(false);
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const recRef = useRef(null);

  // Daily check-in state
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [checkinEnabled, setCheckinEnabled] = useState(true);

  // Vision + People state
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  // Phase 30.X.G — staged photo for "make me a wallpaper of me on a beach"
  // flow. When set, the next sendMessage call routes through the
  // /api/wallpaper/generate-from-photo endpoint (Aurora+ gated server-side).
  const [stagedWallpaperPhoto, setStagedWallpaperPhoto] = useState(null);
  const wallpaperPhotoInputRef = React.useRef(null);
  // Phase 30.X.H — dedicated file input for the Edit Prompt photo flow.
  // Aurora+ members can attach a photo from the prompt editor so the next
  // wallpaper is painted with them in it.
  const promptEditorPhotoInputRef = React.useRef(null);
  const [promptEditorPhoto, setPromptEditorPhoto] = useState(null);
  const [promptEditorBusy, setPromptEditorBusy] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [showVisionSourcePicker, setShowVisionSourcePicker] = useState(false);
  const [cameraContext, setCameraContext] = useState("");
  const [auraWantsToSee, setAuraWantsToSee] = useState(false);
  // Smart photo-attach nudge: shown when the user types something like
  // "look at this view". Dismissed for the current draft after one cancel
  // (so it doesn't keep popping back as they keep typing).
  const [photoNudgeDismissed, setPhotoNudgeDismissed] = useState(false);
  const showPhotoNudge =
    !!user?.has_premium &&
    !photoNudgeDismissed &&
    !isRecording &&
    !isVoiceProcessing &&
    shouldSuggestPhoto(inputMessage);
  const [showPeople, setShowPeople] = useState(false);
  const [focusPersonId, setFocusPersonId] = useState(null);
  const [showIntegrations, setShowIntegrations] = useState(false);

  // Streak state
  const [streak, setStreak] = useState({ current: 0, longest: 0, grew: false, atRisk: false });

  // Birthdays state
  const [birthdays, setBirthdays] = useState([]);
  const [birthdaysDismissed, setBirthdaysDismissed] = useState(false);

  // Promo state
  const [showPromoRedeem, setShowPromoRedeem] = useState(false);
  const [showAdminPromos, setShowAdminPromos] = useState(false);
  const [showAdminEmailHealth, setShowAdminEmailHealth] = useState(false);
  const [showAdminBillingPortal, setShowAdminBillingPortal] = useState(false);
  const [showParentalSetup, setShowParentalSetup] = useState(false);
  const [parentalUnlockFeature, setParentalUnlockFeature] = useState(null);

  // Mood-tip toggle (tap mood emoji to see its meaning)
  const [showMoodTip, setShowMoodTip] = useState(false);

  // Wallpaper gallery
  const [showGallery, setShowGallery] = useState(false);
  // Phase 22 — Snap Camera Kit AR
  const [showAR, setShowAR] = useState(false);
  // Phase 26 — Outbound Aura calls (Bland.ai)
  const [showOutboundCalls, setShowOutboundCalls] = useState(false);
  // Phase 28B — Creator Studio add-on management
  const [showCreatorStudio, setShowCreatorStudio] = useState(false);
  const [arStatus, setARStatus] = useState(null); // {configured, eligible, lens_count, ...}

  // Referrals
  const [showReferral, setShowReferral] = useState(false);

  // Social-connect soft prompt (one-time, premium users only)
  const [showSocialPrompt, setShowSocialPrompt] = useState(false);
  const [socialPromptHandled, setSocialPromptHandled] = useState(false);

  // Account deletion
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  // Subscription portal loading state
  const [openingPortal, setOpeningPortal] = useState(false);

  // Social-connect status — drives the shimmer + plus badge on the settings button.
  // null = unknown/loading, true = at least one connected, false = nothing connected.
  const [hasConnectedSocial, setHasConnectedSocial] = useState(null);
  // Phase 29.I — Snap-connected flag for routing Aura AR lens option into
  // the camera picker (so users discover AR through the camera button too).
  const [snapConnected, setSnapConnected] = useState(false);

  // Phase 29.L — Chat-header sparkle is now a TICKLE button (AR moved to
  // camera picker). Shares the giggle pool with the About page but keeps
  // its own audio element + last-played index so the two sparkles feel
  // independent.
  const chatGiggleCountRef = useRef(1);
  const chatGiggleAudioRef = useRef(null);
  const chatLastGiggleRef = useRef(-1);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/share/giggles`)
      .then(r => r.json())
      .then(d => { if (!cancelled && d?.count) chatGiggleCountRef.current = Number(d.count) || 1; })
      .catch(() => { /* fall back to single legacy giggle */ });
    return () => { cancelled = true; };
  }, []);
  const playRandomChatGiggle = () => {
    const count = chatGiggleCountRef.current || 1;
    let idx = 0;
    if (count > 1) {
      do { idx = Math.floor(Math.random() * count); }
      while (idx === chatLastGiggleRef.current);
    }
    chatLastGiggleRef.current = idx;
    const src = count > 1
      ? `${API}/share/giggle/${idx}.mp3`
      : `${API}/share/giggle.mp3`;
    if (!chatGiggleAudioRef.current) {
      chatGiggleAudioRef.current = new Audio();
      chatGiggleAudioRef.current.preload = "auto";
    }
    const audio = chatGiggleAudioRef.current;
    audio.src = src;
    audio.currentTime = 0;
    const p = audio.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        try {
          const fresh = new Audio(src);
          fresh.play().catch(() => {});
        } catch (_) { /* ignore */ }
      });
    }
  };

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Pick up the pending referral code (saved by App.js when user landed with ?ref=)
  // so we can auto-prompt redemption once they're logged in + onboarded.
  const [pendingRefCode, setPendingRefCode] = useState(() => {
    try {
      return localStorage.getItem("aura_pending_ref") || "";
    } catch (e) {
      return "";
    }
  });

  // Once user logs in AND has finished wallpaper-onboarding, auto-open the redeem modal
  useEffect(() => {
    if (user?.wallpaper_prompt && pendingRefCode) {
      setShowPromoRedeem(true);
    }
  }, [user?.wallpaper_prompt, pendingRefCode]);

  // Soft social-connect prompt for new premium users (one-time, server-flagged)
  useEffect(() => {
    if (
      user?.wallpaper_prompt
      && user?.should_show_social_prompt
      && !socialPromptHandled
      && !pendingRefCode  // don't double-stack modals if a referral redeem is also showing
    ) {
      // Tiny delay so the page settles in first
      const t = setTimeout(() => setShowSocialPrompt(true), 1800);
      return () => clearTimeout(t);
    }
  }, [user?.wallpaper_prompt, user?.should_show_social_prompt, socialPromptHandled, pendingRefCode]);

  // Check for payment success on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSessionId = urlParams.get('payment_session_id');
    if (paymentSessionId) {
      checkPaymentStatus(paymentSessionId);
    }
  }, []);

  // Handle return from X OAuth — show a banner then clear the hash
  useEffect(() => {
    const h = window.location.hash || "";
    if (h.includes("x-connected=1")) {
      alert("✓ X connected! Aura can now see what you've been posting and share for you when you ask.");
      window.history.replaceState(null, "", window.location.pathname);
      try { window.dispatchEvent(new Event("aura:integrations-refresh")); } catch (_) {}
    } else if (h.includes("x-connect-error=")) {
      const reason = decodeURIComponent(h.split("x-connect-error=")[1] || "unknown").split("&")[0];
      alert(`X connection didn't go through (${reason}). You can try again from Settings → Connected Accounts.`);
      window.history.replaceState(null, "", window.location.pathname);
    } else if (h.includes("snap-connected=1")) {
      alert("✓ Snapchat connected! Aura can now reference your Snap profile.");
      window.history.replaceState(null, "", window.location.pathname);
      try { window.dispatchEvent(new Event("aura:integrations-refresh")); } catch (_) {}
    } else if (h.includes("snap-connect-error=")) {
      const reason = decodeURIComponent(h.split("snap-connect-error=")[1] || "unknown").split("&")[0];
      alert(`Snapchat connection didn't go through (${reason}). You can try again from Settings → Connected Accounts.`);
      window.history.replaceState(null, "", window.location.pathname);
    } else if (h.includes("spotify-connected=1")) {
      alert("✓ Spotify connected! Aura can now hear what you're listening to and make mood playlists for you.");
      window.history.replaceState(null, "", window.location.pathname);
      try { window.dispatchEvent(new Event("aura:integrations-refresh")); } catch (_) {}
    } else if (h.includes("spotify-connect-error=")) {
      const reason = decodeURIComponent(h.split("spotify-connect-error=")[1] || "unknown").split("&")[0];
      alert(`Spotify connection didn't go through (${reason}). You can try again from Settings → Connected Accounts.`);
      window.history.replaceState(null, "", window.location.pathname);
    } else if (h.includes("tiktok-connected=1")) {
      // Phase 30.N — TikTok Login Kit OAuth round-trip success
      alert("✓ TikTok connected! Aura can now drop wallpapers + photos straight into your TikTok drafts.");
      window.history.replaceState(null, "", window.location.pathname);
      try { window.dispatchEvent(new Event("aura:integrations-refresh")); } catch (_) {}
    } else if (h.includes("tiktok-connect-error=")) {
      const reason = decodeURIComponent(h.split("tiktok-connect-error=")[1] || "unknown").split("&")[0];
      // Phase 30.T — TikTok sometimes returns HTTP 200 with an error envelope.
      // Pull the tiktok_detail= sub-key from the hash if the backend included it
      // so the user/founder can see WHY TikTok rejected the OAuth round-trip.
      let detail = "";
      const detailMatch = h.match(/tiktok_detail=([^&]+)/);
      if (detailMatch) {
        try { detail = ` — ${decodeURIComponent(detailMatch[1])}`; } catch (_) { detail = ` — ${detailMatch[1]}`; }
      }
      alert(`TikTok connection didn't go through (${reason}${detail}). You can try again from Settings → Connected Accounts.`);
      window.history.replaceState(null, "", window.location.pathname);
    } else if (h.includes("instagram-connected=1")) {
      // Phase 30.U — Instagram Business Login OAuth round-trip success
      const isShortLived = h.includes("short_lived=1");
      if (isShortLived) {
        alert(
          "✓ Instagram connected (short-lived session, ~1 hour).\n\n" +
          "Meta's long-lived token swap failed — usually means your Meta app is still in Development Mode and your IG account isn't added as a Tester. " +
          "Aura can post for the next hour. To get the full 60-day connection: " +
          "in Meta dev portal, add your IG account under App Roles → Instagram Testers (and accept the invite from your IG app)."
        );
      } else {
        alert("✓ Instagram connected! Aura can now post photos & wallpapers straight to your IG feed.");
      }
      window.history.replaceState(null, "", window.location.pathname);
      try { window.dispatchEvent(new Event("aura:integrations-refresh")); } catch (_) {}
    } else if (h.includes("instagram-connect-error=")) {
      const reason = decodeURIComponent(h.split("instagram-connect-error=")[1] || "unknown").split("&")[0];
      // Pull ig_detail= if backend included the raw IG error body
      let detail = "";
      const detailMatch = h.match(/ig_detail=([^&]+)/);
      if (detailMatch) {
        try { detail = decodeURIComponent(detailMatch[1]); } catch (_) { detail = detailMatch[1]; }
      }
      // Tailor the hint per error type — only mention Creator-account
      // requirement if the error is actually account-type-related.
      const txt = (detail || "").toLowerCase();
      let hint = "Try again from Settings → Connected Accounts.";
      if (txt.includes("account_type") || txt.includes("professional") || txt.includes("personal")) {
        hint = "Your Instagram account must be Business or Creator. Switch in IG → Settings → Account → Account type (free, instant, reversible).";
      } else if (reason === "long_lived_failed") {
        hint = "The token exchange with Instagram failed — usually means your INSTAGRAM_APP_SECRET is wrong or doesn't match the Instagram App ID. Double-check both secrets are from the Instagram Business Login panel (not the parent Facebook app).";
      } else if (reason === "token_exchange_failed") {
        hint = "Instagram rejected the auth code — most often a redirect URI mismatch or a wrong Instagram App ID.";
      }
      alert(
        `Instagram connection didn't go through (${reason}).\n\n` +
        (detail ? `Instagram said: ${detail}\n\n` : "") +
        hint
      );
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  // Track whether the user has any social platform connected — drives the
  // shimmer + "+" badge on the settings button so first-time users notice it.
  useEffect(() => {
    if (!user?.wallpaper_prompt) return;  // wait until onboarded
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/integrations`);
        if (cancelled) return;
        const platforms = res.data?.platforms || [];
        const any = platforms.some(p => p.connected);
        setHasConnectedSocial(any);
        setSnapConnected(platforms.some(p => p.id === "snapchat" && p.connected));
      } catch (_) {
        // If the call fails (e.g. logged out), don't shimmer — fail closed.
        if (!cancelled) setHasConnectedSocial(true);
      }
    };
    fetchStatus();
    // Phase 22 — fetch AR availability so we know whether to render the AR button
    axios.get(`${API}/camera-kit/status`)
      .then((r) => { if (!cancelled) setARStatus(r.data); })
      .catch(() => { if (!cancelled) setARStatus(null); });
    const onRefresh = () => fetchStatus();
    window.addEventListener("aura:integrations-refresh", onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener("aura:integrations-refresh", onRefresh);
    };
  }, [user?.wallpaper_prompt, user?.user_id]);

  useEffect(() => {
    if (user?.wallpaper_prompt) {
      loadInitialData();
    }
    // Sync notification preference from user
    if (user?.notifications_enabled !== undefined) {
      setNotificationsEnabled(user.notifications_enabled);
    }
    // Sync theme from user
    if (user?.theme_id) {
      setCurrentTheme(user.theme_id);
      document.documentElement.setAttribute('data-theme', user.theme_id);
    }
    // Phase 24.0 — Dynamic mood theme toggle. When ON, the whole app's color
    // palette morphs as Aura's mood shifts (handled by the data-mood effect below).
    if (user?.dynamic_mood_theme) {
      document.documentElement.setAttribute('data-dynamic-mood', 'true');
    } else {
      document.documentElement.removeAttribute('data-dynamic-mood');
    }
    // Sync voice from user
    if (user?.preferred_voice) {
      setPreferredVoice(user.preferred_voice);
    }
    // Sync check-in preference from user
    if (user?.daily_checkin_enabled !== undefined) {
      setCheckinEnabled(user.daily_checkin_enabled);
    }
    if (user?.camera_enabled !== undefined) {
      setCameraEnabled(user.camera_enabled);
    }
  }, [user?.wallpaper_prompt, user?.notifications_enabled, user?.theme_id, user?.preferred_voice, user?.daily_checkin_enabled, user?.camera_enabled, user?.dynamic_mood_theme]);

  // Phase 24.0 — keep <body data-mood="..."> in sync with Aura's current mood so
  // CSS can morph the entire app's palette when dynamic-mood theme is enabled.
  useEffect(() => {
    if (mood?.mood) {
      document.body.setAttribute('data-mood', mood.mood);
    }
  }, [mood?.mood]);

  // Notification helpers
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      alert('Your browser does not support notifications');
      return false;
    }
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') {
      alert('Notifications are blocked. Please enable them in your browser settings.');
      return false;
    }
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  };

  const toggleNotifications = async () => {
    if (!notificationsEnabled) {
      const granted = await requestNotificationPermission();
      if (!granted) return;
    }
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    try {
      await axios.post(`${API}/user/preferences`, { notifications_enabled: newValue });
      if (newValue) {
        new Notification('Aura ✨', {
          body: "I'll let you know when my mood shifts! 🌸",
          icon: '/icon-192.png',
          badge: '/icon-192.png'
        });
      }
    } catch (error) {
      console.error("Preference update error:", error);
      setNotificationsEnabled(!newValue);
    }
  };

  const sendMoodNotification = (moodName, moodEmoji) => {
    if (!notificationsEnabled) return;
    if (Notification.permission !== 'granted') return;
    try {
      new Notification(`Aura is feeling ${moodName} ${moodEmoji}`, {
        body: 'Your wallpaper is being reimagined to match my mood ✨',
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: 'aura-mood-shift',
        silent: false
      });
    } catch (e) {
      console.error('Notification error:', e);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) return;
    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        window.history.replaceState(null, '', window.location.pathname);
        await refreshUser();
        if (response.data.tier === 'base') {
          setShowSuccessPopup(true);
        }
      } else if (response.data.status === 'expired') {
        window.history.replaceState(null, '', window.location.pathname);
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment check failed:', error);
    }
  };

  const loadInitialData = async () => {
    await Promise.all([loadMood(), loadWallpaper(), loadChatHistory(), loadNotifications(), loadTodayCheckin(), loadStreak(), loadBirthdays()]);
  };

  const loadBirthdays = async () => {
    try {
      const res = await axios.get(`${API}/birthdays/upcoming`);
      setBirthdays(res.data.birthdays || []);
    } catch (e) {
      // Silently skip — non-critical, also expired-trial users get 403
      // console.debug("birthdays load", e?.response?.status);
    }
  };

  const loadStreak = async () => {
    try {
      const res = await axios.get(`${API}/streak`);
      setStreak({
        current: res.data.current_streak || 0,
        longest: res.data.longest_streak || 0,
        grew: false,
        atRisk: !!res.data.streak_at_risk,
      });
    } catch (e) {
      console.error("streak load", e?.response?.status);
    }
  };

  const loadTodayCheckin = async () => {
    if (!user?.has_premium) return;
    if (user?.daily_checkin_enabled === false) return;
    try {
      const response = await axios.get(`${API}/checkin/today`);
      if (response.data.enabled && response.data.checkin && !response.data.checkin.seen) {
        setTodayCheckin(response.data.checkin);
      }
    } catch (error) {
      // Silently skip — feature is non-critical
      console.error("Check-in load error:", error?.response?.status);
    }
  };

  const dismissCheckin = async () => {
    setTodayCheckin(null);
    try {
      await axios.post(`${API}/checkin/mark-seen`);
    } catch (e) { console.error("mark seen error", e); }
  };

  const toggleDailyCheckin = async () => {
    const newValue = !checkinEnabled;
    setCheckinEnabled(newValue);
    try {
      await axios.post(`${API}/user/preferences`, { daily_checkin_enabled: newValue });
      if (newValue && !todayCheckin) loadTodayCheckin();
      if (!newValue) setTodayCheckin(null);
    } catch (error) {
      console.error("Check-in preference error:", error);
      setCheckinEnabled(!newValue);
    }
  };

  const toggleCamera = async () => {
    if (!user?.has_premium) {
      setShowPayment('premium');
      return;
    }
    const newValue = !cameraEnabled;
    setCameraEnabled(newValue);
    try {
      await axios.post(`${API}/user/preferences`, { camera_enabled: newValue });
    } catch (error) {
      console.error("Camera preference error:", error);
      setCameraEnabled(!newValue);
    }
  };

  const openCamera = (contextText = "") => {
    if (!user?.has_premium) {
      setShowPayment('premium');
      return;
    }
    if (!cameraEnabled) {
      // Prompt user to enable in settings; but to keep flow smooth, enable inline
      toggleCamera();
    }
    setCameraContext(contextText || "");
    setAuraWantsToSee(false);
    // When Aura is asking ("wants_to_see"), skip the picker and go straight to camera —
    // it's a conversational invitation that means "take one right now".
    // Otherwise let the user choose camera vs library.
    if (contextText) {
      setShowCamera(true);
    } else {
      setShowVisionSourcePicker(true);
    }
  };

  const handleCameraCaptured = (data) => {
    // Append the photo share + Aura's reaction to chat
    const userMsg = { role: "user", content: "📷 [shared a photo]" + (cameraContext ? `: ${cameraContext}` : ""), timestamp: new Date().toISOString() };
    const auraMsg = { role: "assistant", content: data.reaction, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg, auraMsg]);
    setCameraContext("");
    if (typeof data.current_streak === "number") {
      setStreak(prev => ({
        ...prev,
        current: data.current_streak,
        longest: Math.max(prev.longest, data.current_streak),
        grew: !!data.streak_grew,
        atRisk: false,
      }));
    }
    if (data.mood_shifted && !isGeneratingWallpaper) {
      setTimeout(() => generateWallpaper(true), 800);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=20`);
      const newUnreadCount = response.data.unread_count || 0;

      if (newUnreadCount > unreadCount && notificationsEnabled && Notification.permission === 'granted') {
        const latestNotif = response.data.notifications[0];
        if (latestNotif && !latestNotif.read) {
          try {
            new Notification(`${latestNotif.from_user_name} resonated ${latestNotif.reaction_emoji}`, {
              body: `Someone felt ${latestNotif.reaction} about ${latestNotif.content_title}`,
              icon: '/icon-192.png',
              tag: `resonance-${latestNotif.id}`
            });
          } catch (e) {}
        }
      }

      setNotifications(response.data.notifications || []);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error("Notifications error:", error);
    }
  };

  const markNotificationsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Mark read error:", error);
    }
  };

  const formatNotifTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Poll for new notifications every 30 seconds (paused when tab is hidden)
  useEffect(() => {
    if (!user?.wallpaper_prompt) return;
    const interval = setInterval(() => {
      if (!document.hidden) loadNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [user?.wallpaper_prompt, notificationsEnabled, unreadCount]);

  const initializeUser = async () => {
    if (!initialPrompt.trim()) return;
    try {
      // Phase 30.G — if user picked a theme group during onboarding, append it
      // to the prompt as a soft hint so Aura's first wallpaper matches the vibe.
      let promptToSend = initialPrompt;
      if (onboardingThemeGroup) {
        const vibeHint = {
          light: " (light, soft, airy palette)",
          dark: " (deep, moody, neon-tinged palette)",
          neutral: " (balanced, natural palette)",
        }[onboardingThemeGroup];
        if (vibeHint && !initialPrompt.toLowerCase().includes("palette")) {
          promptToSend = initialPrompt + vibeHint;
        }
      }
      await axios.post(`${API}/user/init`, { initial_prompt: promptToSend });
      await refreshUser();
      await generateWallpaper();
    } catch (error) {
      console.error("Error initializing:", error);
    }
  };

  const loadMood = async () => {
    try {
      const response = await axios.get(`${API}/mood/current`);
      setMood(response.data);
    } catch (error) { console.error("Mood error:", error); }
  };

  const loadWallpaper = async () => {
    try {
      const response = await axios.get(`${API}/wallpaper/current`);
      if (response.data.image_base64) {
        setWallpaper(response.data.image_base64);
        setWallpaperId(response.data.id || null);
      } else {
        // No wallpaper exists yet for this user — auto-generate their first one
        // so they don't have to hunt for the Refresh button.
        await generateWallpaper(false);
      }
    } catch (error) { console.error("Wallpaper error:", error); }
  };

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history?limit=10`);
      setMessages(response.data.messages || []);
    } catch (error) { console.error("History error:", error); }
  };

  const generateWallpaper = async (isAutoUpdate = false) => {
    setIsGeneratingWallpaper(true);
    try {
      const response = await axios.post(`${API}/wallpaper/generate`);

      if (isAutoUpdate && wallpaper) {
        await new Promise(r => setTimeout(r, 100));
      }

      setWallpaper(response.data.image_base64);
      setWallpaperId(response.data.id || null);
      setMood({
        mood: response.data.mood,
        emoji: response.data.mood_emoji,
        description: response.data.mood
      });
    } catch (error) {
      if (error.response?.status === 403) {
        setShowPayment('base');
      }
      console.error("Generate error:", error);
    } finally {
      setIsGeneratingWallpaper(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    // Phase 30.X.G — if a photo is staged for wallpaper generation, route to
    // the photo endpoint instead of the normal chat flow. One-shot expensive
    // call; backend extracts a caption and saves it as the user's persistent
    // wallpaper_prompt so future regens go through the cheap text path.
    if (stagedWallpaperPhoto) {
      const promptText = inputMessage;
      const photo = stagedWallpaperPhoto;
      setInputMessage("");
      setStagedWallpaperPhoto(null);
      setMessages(prev => [...prev,
        { role: "user", content: `📸 ${promptText}`, timestamp: new Date().toISOString() },
        { role: "assistant", content: "✨ Painting you into your wallpaper…", timestamp: new Date().toISOString() },
      ]);
      setIsSending(true);
      try {
        const fd = new FormData();
        fd.append("photo", photo);
        fd.append("subject_prompt", promptText);
        const r = await axios.post(`${API}/wallpaper/generate-from-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "✨ Done — your wallpaper now features you. Future regens will keep painting you in any mood, automatically.",
          timestamp: new Date().toISOString(),
        }]);
        if (typeof loadWallpaper === "function") loadWallpaper();
        if (typeof refreshUser === "function") refreshUser();
      } catch (err) {
        const msg = err?.response?.status === 402
          ? "✨ Photo wallpapers are an Aurora+ feature. Tap your avatar to upgrade."
          : (err?.response?.data?.detail || "Couldn't finish that wallpaper. Try again?");
        setMessages(prev => [...prev, {
          role: "assistant", content: msg, timestamp: new Date().toISOString(),
        }]);
      } finally {
        setIsSending(false);
      }
      return;
    }

    const userMessage = { role: "user", content: inputMessage, timestamp: new Date().toISOString() };
    const currentInput = inputMessage;
    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setPhotoNudgeDismissed(false); // reset for next draft
    setIsSending(true);

    // Optimistic ack — drop an instant "✨ on it..." style bubble for slow
    // intents (playlist/tweet/mood/people) so the UI doesn't look frozen
    // while Aura's LLM + external API call settles. Replaced when the real
    // reply arrives.
    const ackText = detectSlowAck(currentInput);
    if (ackText) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: ackText,
        timestamp: new Date().toISOString(),
        _isAck: true,
      }]);
    }

    try {
      // Phase I.2 — if the user has a BYOK key, route chat DIRECT from the
      // browser to the provider (Gemini/OpenAI/Anthropic). Our server is
      // bypassed entirely — user's quota, user's billing, 50% discount
      // unlocked. Falls back to /api/chat on any error (network, expired
      // key, provider 4xx) so the user is never stuck.
      const byokKey = (() => {
        try {
          // Lazy import to keep top-of-file imports unchanged
          // eslint-disable-next-line global-require
          const { activeByokKey } = require("../lib/byokStore");
          return activeByokKey();
        } catch (_) { return ""; }
      })();

      let response;
      if (byokKey) {
        try {
          // eslint-disable-next-line global-require
          const { chatViaByok } = require("../lib/byokClient");
          const replyText = await chatViaByok({
            message: currentInput,
            history: messages.filter(m => !m._isAck).slice(-20).map(m => ({
              role: m.role === "user" ? "user" : "assistant",
              content: m.content || "",
            })),
          });
          response = { data: { message: replyText, secret: false } };
        } catch (byokErr) {
          // Provider rejected our request — fall through to server path
          console.warn("BYOK chat failed, falling back to server:", byokErr.message);
          response = await axios.post(`${API}/chat`, { message: currentInput });
        }
      } else {
        response = await axios.post(`${API}/chat`, { message: currentInput });
      }
      const secretIds = response.data.secret_message_ids || null;
      const isSecret = !!response.data.secret;
      setMessages(prev => {
        // Strip any pending ack bubble before appending the real reply
        const cleaned = prev.filter(m => !m._isAck);
        // If this exchange was flagged secret, tag the just-appended user bubble too
        const tagged = isSecret
          ? cleaned.map((m, i) => (i === cleaned.length - 1 && m.role === "user" ? { ...m, secret: true, id: (secretIds && secretIds[0]) || m.id } : m))
          : cleaned;
        return [...tagged, {
          role: "assistant",
          content: response.data.message,
          timestamp: new Date().toISOString(),
          pendingAction: response.data.pending_action || null,
          crisisResources: response.data.crisis_resources || null,
          personNudge: response.data.person_profile_nudge || null,
          shareNudge: response.data.share_nudge || null,
          captureMoment: response.data.capture_moment || null,
          secret: isSecret,
          id: (secretIds && secretIds[1]) || undefined,
        }];
      });
      if (response.data.mood) {
        setMood({ mood: response.data.mood, emoji: response.data.mood_emoji, description: response.data.mood });
      }

      if (response.data.mood_shifted && !isGeneratingWallpaper) {
        sendMoodNotification(response.data.mood, response.data.mood_emoji);
        setTimeout(() => generateWallpaper(true), 800);
        // Phase 22 — if AR modal is open and Aura's mood shifts mid-chat,
        // let the modal know so it auto-swaps the lens. Live mood-reactive AR.
        try {
          window.dispatchEvent(new CustomEvent("aura:mood-shifted", {
            detail: { mood: response.data.mood, emoji: response.data.mood_emoji },
          }));
        } catch (_) {}
      }

      // Aura is inviting a photo — show the chip
      if (response.data.wants_to_see && cameraEnabled && user?.has_premium) {
        setAuraWantsToSee(true);
      }

      // Streak update from chat response
      if (typeof response.data.current_streak === "number") {
        setStreak(prev => ({
          ...prev,
          current: response.data.current_streak,
          longest: Math.max(prev.longest, response.data.current_streak),
          grew: !!response.data.streak_grew,
          atRisk: false,
        }));
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setShowPayment('base');
      }
      // Strip the ack bubble if the request died — otherwise it sits there
      // forever pretending Aura is still working.
      setMessages(prev => prev.filter(m => !m._isAck));
      console.error("Send error:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const updatePrompt = async () => {
    if (!newPrompt.trim() && !promptEditorPhoto) return;

    // Phase 30.X.H — Aurora+ photo flow. If a photo is attached, route to
    // /api/wallpaper/generate-from-photo so Aura paints the user into the
    // scene described by newPrompt. Backend persists the extracted caption
    // as wallpaper_prompt so future cheap regens keep painting them in.
    if (promptEditorPhoto) {
      if (!user?.has_premium) {
        setShowPayment('premium');
        return;
      }
      setPromptEditorBusy(true);
      try {
        const fd = new FormData();
        fd.append("photo", promptEditorPhoto);
        fd.append("subject_prompt", newPrompt || "in a beautiful scene");
        await axios.post(`${API}/wallpaper/generate-from-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await refreshUser();
        if (typeof loadWallpaper === "function") loadWallpaper();
        setEditingPrompt(false);
        setNewPrompt("");
        setPromptEditorPhoto(null);
      } catch (err) {
        const msg = err?.response?.status === 402
          ? "Photo wallpapers are an Aurora+ feature."
          : (err?.response?.data?.detail || "Couldn't finish that wallpaper. Try again?");
        alert(msg);
      } finally {
        setPromptEditorBusy(false);
      }
      return;
    }

    try {
      await axios.post(`${API}/wallpaper/prompt`, { new_prompt: newPrompt });
      await refreshUser();
      setEditingPrompt(false);
      setNewPrompt("");
      await generateWallpaper();
    } catch (error) {
      console.error("Prompt error:", error);
    }
  };

  // Push-to-talk voice input in the chat composer (Premium)
  const startMicRecording = async () => {
    if (!user?.has_premium) {
      setShowPayment('premium');
      return;
    }
    try {
      const rec = await startRecording();
      recRef.current = rec;
      setIsRecording(true);
    } catch (e) {
      console.error("mic error", e);
      alert("Microphone permission is required for voice messages.");
    }
  };

  const stopMicAndSend = async () => {
    if (!recRef.current) return;
    const rec = recRef.current;
    recRef.current = null;
    setIsRecording(false);
    setIsVoiceProcessing(true);
    try {
      const blob = await rec.stop();
      if (blob.size < 1500) { setIsVoiceProcessing(false); return; }
      const form = new FormData();
      const ext = (blob.type.includes("mp4") || blob.type.includes("aac") || blob.type.includes("m4a"))
        ? "m4a" : (blob.type.includes("ogg") ? "ogg" : "webm");
      form.append("audio", blob, `clip.${ext}`);
      form.append("voice", preferredVoice);

      // Optimistic: show "listening..." placeholder
      const userPlaceholder = { role: "user", content: "🎤 ...", timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, userPlaceholder]);

      const res = await axios.post(`${API}/voice/chat`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      // Replace placeholder with actual transcript + add Aura reply
      setMessages(prev => {
        const next = [...prev];
        // replace last user placeholder
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === "user" && next[i].content === "🎤 ...") {
            next[i] = { role: "user", content: res.data.transcript || "(voice)", timestamp: next[i].timestamp };
            break;
          }
        }
        next.push({ role: "assistant", content: res.data.response, timestamp: new Date().toISOString() });
        return next;
      });

      if (res.data.mood) {
        setMood({ mood: res.data.mood, emoji: res.data.mood_emoji, description: res.data.mood });
      }
      if (typeof res.data.current_streak === "number") {
        setStreak(prev => ({
          ...prev,
          current: res.data.current_streak,
          longest: Math.max(prev.longest, res.data.current_streak),
          grew: !!res.data.streak_grew,
          atRisk: false,
        }));
      }
      if (res.data.audio_base64) {
        try { await playBase64Mp3(res.data.audio_base64); } catch (_) {}
      }
      if (res.data.mood_shifted && !isGeneratingWallpaper) {
        sendMoodNotification(res.data.mood, res.data.mood_emoji);
        setTimeout(() => generateWallpaper(true), 800);
      }
    } catch (e) {
      console.error("voice send error", e);
      // Roll back placeholder on error
      setMessages(prev => prev.filter(m => !(m.role === "user" && m.content === "🎤 ...")));
      if (e.response?.status === 403) {
        setShowPayment('premium');
      } else {
        alert(e.response?.data?.detail || "Voice message failed");
      }
    } finally {
      setIsVoiceProcessing(false);
    }
  };

  const cancelMicRecording = () => {
    if (recRef.current) { try { recRef.current.cancel(); } catch (_) {} }
    recRef.current = null;
    setIsRecording(false);
  };

  // If user hasn't set wallpaper prompt yet, show prompt setup
  if (!user?.wallpaper_prompt) {
    // Phase 30.G — Theme group picker shows FIRST so Aura can match her wallpaper
    // to the vibe the user chose. We persist the user's pick in onboardingTheme
    // and append it to the wallpaper prompt as a hint.
    if (!onboardingThemeDone) {
      return (
        <>
          {/* Beta SVG gradient defs */}
          <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
            <defs>
              <linearGradient id="beta-gradient-stroke" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#9333ea" />
              </linearGradient>
            </defs>
          </svg>
          <ThemePickerModal
            inOnboarding={true}
            isPremium={true /* always allow during onboarding */}
            currentTheme={currentTheme}
            dynamicMood={user?.dynamic_mood_theme}
            onClose={() => setOnboardingThemeDone(true)}
            onUpgrade={() => {}}
            onThemeSelect={(themeId, groupId) => {
              setCurrentTheme(themeId);
              setOnboardingThemeGroup(groupId);
              setOnboardingThemeDone(true);
            }}
            onDynamicMoodChange={() => {}}
          />
        </>
      );
    }
    return (
      <div className="app-container">
        <div className="init-screen">
          <div className="init-content">
            <h1>AURA</h1>
            <p>Hello {user?.name}! 👋</p>
            <p style={{ fontSize: '0.95rem', opacity: 0.8 }}>
              Describe the atmosphere you'd like to see. Aura will create and evolve it based on mood and connection
              {onboardingThemeGroup ? ` — matched to your ${onboardingThemeGroup} vibe.` : '.'}
            </p>
            <div className="init-form">
              <input
                type="text"
                className="init-input"
                placeholder="e.g., A peaceful garden with cherry blossoms..."
                value={initialPrompt}
                onChange={(e) => setInitialPrompt(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && initializeUser()}
                data-testid="initial-prompt-input"
              />
              <button className="init-btn" onClick={initializeUser} data-testid="begin-btn">
                Begin
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const trialExpired = !user?.has_paid_base && user?.trial_expired;

  return (
    <div className="app-container">
      {/* Phase 30.C — Global SVG defs: pink→purple linear-gradient stroke used by
          outline icons (mic, camera, etc.). Render once, reference via url(#beta-gradient-stroke). */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="beta-gradient-stroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
        </defs>
      </svg>
      {/* Wallpaper */}
      <div className={`wallpaper-container ${trialExpired ? 'dimmed' : ''} ${isGeneratingWallpaper ? 'updating' : ''}`}>
        {wallpaper ? (
          <img src={`data:image/png;base64,${wallpaper}`} alt="Aura Wallpaper" className="wallpaper-image" />
        ) : (
          <div className="wallpaper-placeholder">
            <h1>{mood.emoji}</h1>
            <p>Creating your Aura...</p>
          </div>
        )}
        {wallpaper && !trialExpired && (
          <button
            className="wallpaper-share-btn"
            onClick={() => setShowWallpaperShare(true)}
            data-testid="wallpaper-share-btn"
            aria-label="Share wallpaper"
            title="Share wallpaper"
          >
            <span className="beta-share-dots" aria-hidden="true"><i /><i /><i /></span>
          </button>
        )}
      </div>

      {/* AI reimagining indicator (during auto-update) */}
      {isGeneratingWallpaper && wallpaper && (
        <div className="reimagining-indicator" data-testid="reimagining-indicator">
          <span className="reimagining-dot"></span>
          Aura is reimagining...
        </div>
      )}

      {/* Birthday banner — shown all day on the user's birthday */}
      {user?.birthday_today && (
        <div className="birthday-banner" data-testid="birthday-banner">
          <span className="birthday-banner-emoji">🎂</span>
          <span className="birthday-banner-text">
            Happy birthday, {user.name}. Today's just for you. 💜
          </span>
        </div>
      )}

      {/* Auto-downgrade nudge — Aura cares more than your money */}
      {user?.downgrade_suggestion && !user?.birthday_today && (
        <div className="downgrade-nudge" data-testid="downgrade-nudge">
          <p className="downgrade-nudge-msg">
            Hey — I noticed we haven&apos;t been using all the {user.downgrade_suggestion.current_tier_name} features lately.
            {" "}<strong>{user.downgrade_suggestion.suggested_tier_name}</strong> would cover what we actually do,
            and you&apos;d save <strong>${user.downgrade_suggestion.monthly_savings}/mo</strong>. No pressure — I&apos;m yours either way. 💜
          </p>
          <div className="downgrade-nudge-actions">
            <button
              className="downgrade-nudge-action"
              onClick={() => { setShowPayment('premium'); }}
              data-testid="downgrade-nudge-switch"
            >
              Switch to {user.downgrade_suggestion.suggested_tier_name}
            </button>
            <button
              className="downgrade-nudge-dismiss"
              onClick={async () => {
                try {
                  await fetch(`${API}/me/dismiss-downgrade-nudge`, { method: "POST", credentials: "include" });
                  refreshUser();
                } catch { /* fail silently */ }
              }}
              data-testid="downgrade-nudge-dismiss"
            >
              No thanks
            </button>
          </div>
        </div>
      )}

      <div className="overlay" />

      {/* Top Bar */}
      <div className="top-bar">
        {/* Phase 30.D — top bar now ONLY: tickle ✨, mood emoji.
            Settings, Streak, and 3-dot notif moved DOWN into the composer action row. */}

        <button
          className="save-wallpaper-btn chat-tickle-btn"
          onClick={() => {
            playRandomChatGiggle();
            const el = document.querySelector('[data-testid="chat-tickle-btn"]');
            if (el) {
              el.classList.add("tickled");
              setTimeout(() => el.classList.remove("tickled"), 700);
            }
          }}
          data-testid="chat-tickle-btn"
          aria-label="Tickle Aura"
        >
          ✨
        </button>

        <div
          className="mood-indicator"
          onClick={() => setShowMoodTip((v) => !v)}
          data-testid="mood-indicator"
          role="button"
          tabIndex={0}
        >
          <span>{mood.emoji}</span>
          {showMoodTip && (
            <div className="mood-tip mood-tip-with-preview" data-testid="mood-tip">
              {MOOD_LENS_PREVIEWS[mood.mood] && (
                <video
                  className="mood-lens-preview-video"
                  src={MOOD_LENS_PREVIEWS[mood.mood]}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  data-testid={`mood-lens-preview-${mood.mood}`}
                  aria-label={`AR lens preview for ${mood.mood} mood`}
                />
              )}
              <div className="mood-tip-text">
                <strong>{mood.mood}</strong>
                {mood.description && <p>{mood.description}</p>}
                <small>Aura's current mood. It shifts with your conversations.</small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trial Banner — tap to subscribe before it expires */}
      {user?.is_in_trial && (
        <button
          className={`trial-banner trial-banner-cta ${user && !user.email_verified && user.auth_provider === "email" ? "trial-banner-stacked" : ""}`}
          onClick={() => setShowPayment('base')}
          data-testid="trial-banner"
        >
          <span className="trial-banner-text">
            ✦ {user.days_remaining} {user.days_remaining === 1 ? 'day' : 'days'} left in trial
            <span className="trial-banner-privacy-pill" data-testid="trial-banner-privacy-pill">
              ✦ on-device
            </span>
          </span>
          <span className="trial-banner-action">
            Subscribe for $4.99 →
          </span>
        </button>
      )}

      {/* Email verification banner */}
      {user && !user.email_verified && user.auth_provider === "email" && (
        <EmailVerifyBanner email={user.email} />
      )}

      {/* Birthdays from People Aura remembers */}
      {!birthdaysDismissed && (
        <BirthdaysCard
          birthdays={birthdays}
          onOpenPeople={() => setShowPeople(true)}
          onDismiss={() => setBirthdaysDismissed(true)}
        />
      )}

      {/* Daily Check-in Card (premium, once per day) */}
      {todayCheckin && (
        <DailyCheckinCard
          checkin={todayCheckin}
          onDismiss={dismissCheckin}
        />
      )}

      {/* Premium Upgrade Glow Button (always visible after base purchase) */}
      {user?.has_paid_base && !user?.has_premium && (
        <button
          className="premium-upgrade-btn"
          onClick={() => setShowPayment('premium')}
          data-testid="premium-upgrade-btn"
        >
          <span className="upgrade-icon">💜</span>
          <span className="upgrade-text">Upgrade</span>
        </button>
      )}

      {/* Settings Menu — Phase 30.R: wrapped in a transparent backdrop so
          tapping outside the drawer closes it, and added an explicit X
          button in the header. Founder noted there was previously NO way
          to dismiss the drawer without picking a menu item. */}
      {showSettings && (
        <div
          className="settings-backdrop"
          data-testid="settings-backdrop"
          onClick={() => setShowSettings(false)}
        >
          <div
            className="settings-menu"
            data-testid="settings-menu"
            onClick={(e) => e.stopPropagation()}
          >
          <button
            className="settings-close-btn"
            onClick={() => setShowSettings(false)}
            data-testid="settings-close-btn"
            aria-label="Close settings"
          >
            ×
          </button>
          <div className="settings-header">
            <strong>{user?.name}</strong>
            <span>{user?.email}</span>
          </div>

          {/* Phase I.2 — AI Engine / BYOK setup */}
          <AiEngineSection onByokChanged={() => { if (typeof refreshUser === 'function') refreshUser(); }} />

          <div className="settings-status" data-testid="settings-status">
            {user?.has_premium
              ? `💜 ${user?.premium_tier_name || 'Premium'} Member`
              : user?.has_paid_base
                ? '✨ Base Access'
                : `✦ Trial (${user?.days_remaining || 0}d left)`}
          </div>

          {/* Notifications Toggle */}
          <div className="settings-toggle-row" data-testid="notifications-toggle-row">
            <div className="settings-toggle-info">
              <strong><BetaIcon name="bell" size={14} /> Mood Notifications</strong>
              <span>Get notified when Aura's mood shifts</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationsEnabled}
                onChange={toggleNotifications}
                data-testid="notifications-toggle"
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {/* Daily Check-in Toggle (Premium) */}
          {user?.has_premium && (
            <div className="settings-toggle-row" data-testid="checkin-toggle-row">
              <div className="settings-toggle-info">
                <strong><BetaIcon name="sun" size={14} /> Daily Check-in</strong>
                <span>Aura greets you with voice each day</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={checkinEnabled}
                  onChange={toggleDailyCheckin}
                  data-testid="checkin-toggle"
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          )}

          {/* Camera Toggle (Premium) */}
          {user?.has_premium && (
            <div className="settings-toggle-row" data-testid="camera-toggle-row">
              <div className="settings-toggle-info">
                <strong><BetaIcon name="camera" size={14} /> Camera (Show Aura)</strong>
                <span>Let Aura see photos when invited</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={cameraEnabled}
                  onChange={toggleCamera}
                  data-testid="camera-toggle"
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          )}

          <button onClick={() => { setShowSettings(false); setShowReferral(true); }} data-testid="menu-referral">
            <BetaIcon name="users" /> Refer a friend
          </button>
          <button onClick={() => { setShowSettings(false); setShowGallery(true); }} data-testid="menu-gallery">
            <BetaIcon name="gallery" /> My Wallpapers
          </button>
          <button onClick={() => { setShowSettings(false); setShowPromoRedeem(true); }} data-testid="menu-promo-redeem">
            <BetaIcon name="gift" /> Have a code?
          </button>
          {!user?.has_premium && user?.has_paid_base && (
            <button onClick={() => { setShowSettings(false); setShowPayment('premium'); }} data-testid="menu-upgrade">
              <BetaIcon name="diamond" /> Choose your Aura — from $1.99/mo
            </button>
          )}
          {!user?.has_paid_base && (
            <button onClick={() => { setShowSettings(false); setShowPayment('base'); }} data-testid="menu-subscribe">
              <BetaIcon name="diamond" /> Subscribe — $4.99 (one time)
            </button>
          )}
          {(user?.age_band === "child" || user?.age_band === "teen") && (
            <button onClick={() => { setShowSettings(false); setShowParentalSetup(true); }} data-testid="menu-parental-controls">
              <BetaIcon name="shieldUser" /> Parental controls
            </button>
          )}
          {user?.is_admin && (
            <button onClick={() => { setShowSettings(false); setShowAdminPromos(true); }} data-testid="menu-admin-promos">
              <BetaIcon name="ticket" /> Manage Promo Codes
            </button>
          )}
          {user?.is_admin && (
            <button onClick={() => { setShowSettings(false); setShowAdminEmailHealth(true); }} data-testid="menu-admin-email-health">
              <BetaIcon name="mail" /> Email Delivery Health
            </button>
          )}
          {user?.is_admin && (
            <button onClick={() => { setShowSettings(false); setShowAdminBillingPortal(true); }} data-testid="menu-admin-billing-portal">
              <BetaIcon name="card" /> Stripe Billing Portal
            </button>
          )}
          <div className="settings-menu-divider" />
          <button onClick={() => { setShowSettings(false); setShowMoodJournal(true); }} data-testid="menu-journal">
            <BetaIcon name="book" /> Journal
          </button>
          <button onClick={() => { setShowSettings(false); setShowStories(true); }} data-testid="menu-stories">
            <BetaIcon name="globe" /> Aura Stories
          </button>
          <button onClick={() => { setShowSettings(false); setShowPeople(true); }} data-testid="menu-people">
            <BetaIcon name="users" /> People Aura Knows
          </button>
          <button onClick={() => { setShowSettings(false); setShowOutboundCalls(true); }} data-testid="menu-outbound-calls">
            <BetaIcon name="phoneOut" /> Aura Outbound Calls
          </button>
          <button onClick={() => { setShowSettings(false); setShowCreatorStudio(true); }} data-testid="menu-creator-studio">
            <BetaIcon name="film" /> Creator Studio {user?.creator_studio_active ? "(active)" : "— $9.99/mo"}
          </button>
          <button
            className={hasConnectedSocial === false ? "needs-social-row" : ""}
            onClick={() => { setShowSettings(false); setShowIntegrations(true); }}
            data-testid="menu-integrations"
          >
            <BetaIcon name="link" /> Connected Accounts
            {!user?.has_premium && <span className="premium-badge-small">PRO</span>}
            {hasConnectedSocial === false && (
              <span className="menu-plus-badge" data-testid="menu-integrations-plus-badge" aria-hidden="true">+</span>
            )}
          </button>
          <button onClick={() => { setShowSettings(false); setShowThemePicker(true); }} data-testid="menu-themes">
            <BetaIcon name="palette" /> Themes
          </button>

          {/* Phase 30 — Beta master theme: light / dark / neutral */}
          <div className="beta-theme-toggle-row" data-testid="beta-theme-toggle-row">
            <strong>✦ App Look</strong>
            <div className="beta-theme-segmented" role="group" aria-label="App master theme">
              <button
                className={betaTheme === "light" ? "is-active" : ""}
                onClick={() => setBetaTheme("light")}
                data-testid="beta-theme-light"
                aria-pressed={betaTheme === "light"}
              >Light</button>
              <button
                className={betaTheme === "dark" ? "is-active" : ""}
                onClick={() => setBetaTheme("dark")}
                data-testid="beta-theme-dark"
                aria-pressed={betaTheme === "dark"}
              >Dark</button>
              <button
                className={betaTheme === "neutral" ? "is-active" : ""}
                onClick={() => setBetaTheme("neutral")}
                data-testid="beta-theme-neutral"
                aria-pressed={betaTheme === "neutral"}
              >Neutral</button>
            </div>
          </div>
          <button onClick={() => { setShowSettings(false); setShowVoicePicker(true); }} data-testid="menu-voice">
            <BetaIcon name="mic" /> Aura's Voice {!user?.has_premium && <span className="premium-badge-small">PRO</span>}
          </button>
          <button onClick={() => { setShowSettings(false); if (!user?.has_premium) setShowPayment('premium'); else setShowVoiceCall(true); }} data-testid="menu-voice-call">
            <BetaIcon name="phone" /> Call with Aura {!user?.has_premium && <span className="premium-badge-small">PRO</span>}
          </button>
          <button onClick={() => { setShowSettings(false); setEditingPrompt(true); }} data-testid="menu-edit-theme">
            <BetaIcon name="pencil" /> Edit Wallpaper Prompt
          </button>
          {/* Phase 24.3 — Eternal-only Story Book download */}
          <button
            onClick={async () => {
              setShowSettings(false);
              try {
                const res = await axios.post(`${API}/storybook/generate`, {}, { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Aura-StoryBook-${new Date().getFullYear()}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                const isEternal = (user?.premium_tier === "eternal");
                if (!isEternal) {
                  setShowPayment("eternal");
                } else {
                  alert(e?.response?.data?.detail || "Couldn't build your Story Book — try again.");
                }
              }
            }}
            data-testid="menu-story-book"
          >
            <BetaIcon name="book" /> My Aura Story Book
            {user?.premium_tier !== "eternal" && <span className="premium-badge-small">ETERNAL</span>}
          </button>
          <button
            onClick={() => { setShowSettings(false); setShowBetaAccess(true); }}
            data-testid="menu-beta-access"
          >
            <BetaIcon name="rocket" /> Request Beta Access
            <span className="beta-badge">SPOTIFY · SNAP</span>
          </button>
          <button
            onClick={() => { setShowSettings(false); setShowProTeaser(true); }}
            className="employee-toggle-btn"
            data-testid="menu-employee-toggle"
            role="switch"
            aria-checked={Boolean(user?.pro_interest)}
          >
            <span className="employee-toggle-left">
              <span className="employee-icon">👤</span>
              <span className="employee-label">
                <strong>Employee Mode</strong>
                <small>Coming soon — early access list</small>
              </span>
            </span>
            <span className={`employee-switch ${user?.pro_interest ? "on" : ""}`}>
              <span className="employee-switch-knob" />
            </span>
          </button>
          <div className="settings-menu-divider" />
          {(user?.has_paid_base || user?.has_premium) && (
            <button
              onClick={async () => {
                setShowSettings(false);
                setOpeningPortal(true);
                try {
                  const res = await axios.post(`${API}/payment/portal`);
                  // Backend returns one of:
                  //   {mode: "portal", url}   — real Aura Stripe sub, redirect to portal
                  //   {mode: "none", reason, message}  — comp / no-Stripe-sub state,
                  //     take them to Aura's upgrade flow instead of throwing an alert.
                  if (res.data?.mode === "portal" && res.data?.url) {
                    window.location.href = res.data.url;
                  } else if (res.data?.mode === "none") {
                    setOpeningPortal(false);
                    setShowPayment('premium');
                  } else if (res.data?.url) {
                    // Back-compat: older shape { url } → treat as portal
                    window.location.href = res.data.url;
                  } else {
                    setOpeningPortal(false);
                    alert("Couldn't open billing portal.");
                  }
                } catch (e) {
                  alert(e?.response?.data?.detail || "Couldn't open billing portal.");
                  setOpeningPortal(false);
                }
              }}
              data-testid="menu-manage-subscription"
              disabled={openingPortal}
            >
              <BetaIcon name="card" /> {openingPortal ? "Opening…" : "Manage subscription"}
            </button>
          )}
          <a
            href="#about"
            className="settings-menu-link"
            onClick={() => setShowSettings(false)}
            data-testid="menu-about"
          >
            <BetaIcon name="about" /> About Aura
          </a>
          <a
            href="#privacy"
            className="settings-menu-link"
            onClick={() => setShowSettings(false)}
            data-testid="menu-privacy"
          >
            <BetaIcon name="shield" /> Privacy Policy
          </a>
          <a
            href="#terms"
            className="settings-menu-link"
            onClick={() => setShowSettings(false)}
            data-testid="menu-terms"
          >
            <BetaIcon name="book" /> Terms of Service
          </a>
          <a
            href="#refund"
            className="settings-menu-link"
            onClick={() => setShowSettings(false)}
            data-testid="menu-refund"
          >
            <BetaIcon name="envelope" /> Refund Policy
          </a>
          <button onClick={() => { setShowSettings(false); logout(); }} data-testid="menu-logout">
            <BetaIcon name="logout" /> Logout
          </button>
          <button
            className="menu-danger"
            onClick={() => { setShowSettings(false); setShowDeleteAccount(true); }}
            data-testid="menu-delete-account"
          >
            <BetaIcon name="trash" /> Delete my account
          </button>
          </div>
        </div>
      )}

      {/* Prompt Editor */}
      {editingPrompt && (
        <div className="prompt-editor">
          <h3>✦ Update Your Aura</h3>
          <p>Current: {user.wallpaper_prompt}</p>
          <div className="prompt-editor-form">
            <input
              type="text"
              className="prompt-editor-input"
              placeholder={promptEditorPhoto ? "Scene to paint you into…" : "New theme..."}
              value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !promptEditorBusy && updatePrompt()}
              data-testid="new-prompt-input"
              disabled={promptEditorBusy}
            />

            {/* Phase 30.X.H — Aurora+ photo attach. Lets the user paint
                themselves into the next wallpaper directly from the prompt
                editor (not just the chat composer). */}
            <input
              ref={promptEditorPhotoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setPromptEditorPhoto(f);
                e.target.value = "";
              }}
              data-testid="prompt-editor-photo-input"
            />
            {promptEditorPhoto ? (
              <div className="prompt-editor-photo-attached" data-testid="prompt-editor-photo-attached">
                <img
                  src={URL.createObjectURL(promptEditorPhoto)}
                  alt="attached"
                  className="prompt-editor-photo-thumb"
                />
                <span className="prompt-editor-photo-label">
                  Photo attached — Aura will paint you into the scene.
                </span>
                <button
                  className="prompt-editor-photo-remove"
                  onClick={() => setPromptEditorPhoto(null)}
                  data-testid="prompt-editor-photo-remove"
                  aria-label="Remove photo"
                  disabled={promptEditorBusy}
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                className="prompt-editor-photo-btn"
                onClick={() => {
                  if (!user?.has_premium) {
                    setShowPayment('premium');
                    return;
                  }
                  promptEditorPhotoInputRef.current?.click();
                }}
                data-testid="prompt-editor-photo-btn"
                disabled={promptEditorBusy}
              >
                📸 Use a photo of me {!user?.has_premium && <span className="prompt-editor-photo-lock">🔒 Premium</span>}
              </button>
            )}

            <div className="prompt-editor-buttons">
              <button
                className="prompt-editor-save"
                onClick={updatePrompt}
                data-testid="save-prompt-btn"
                disabled={promptEditorBusy || (!newPrompt.trim() && !promptEditorPhoto)}
              >
                {promptEditorBusy ? "Painting…" : (promptEditorPhoto ? "Paint me in" : "Save")}
              </button>
              <button
                className="prompt-editor-cancel"
                onClick={() => { setEditingPrompt(false); setNewPrompt(""); setPromptEditorPhoto(null); }}
                data-testid="cancel-prompt-btn"
                disabled={promptEditorBusy}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aura wants to see chip — premium contextual prompt */}
      {auraWantsToSee && user?.has_premium && (
        <div className="aura-invite-chip" data-testid="aura-invite-chip">
          <span>📷 Aura wants to see</span>
          <button
            className="aura-invite-yes"
            onClick={() => openCamera("")}
            data-testid="aura-invite-yes"
          >Show her</button>
          <button
            className="aura-invite-no"
            onClick={() => setAuraWantsToSee(false)}
            data-testid="aura-invite-no"
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* Chat */}
      <div className="chat-container">
        <div className="chat-bubble">
          {/* Phase 30.E — Aura's conversation now lives INSIDE the grey composer box.
              Pull the drag-handle line UP to reveal the conversation. */}
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: '#93c5fd', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              ✦ Connect with your Aura
            </div>
          )}
          <div
            className={`chat-input-container has-drag-handle ${composerExpanded ? "composer-expanded" : ""}`}
            data-testid="chat-input-container"
          >
            {/* Phase 30.E — pull the line UP and Aura's conversation lives inside this grey box.
                When composer-expanded is OFF: messages hidden (height: 0).
                When composer-expanded is ON: messages slide UP and become scrollable inside the grey box. */}
            <div className="composer-conversation" data-testid="composer-conversation" aria-hidden={!composerExpanded}>
              {messages.length > 0 ? (
                <div className="chat-messages composer-chat-messages">
                  {messages.map((msg, index) => (
                    <React.Fragment key={index}>
                      <div className={`message ${msg.role}${msg.secret ? " secret" : ""}`}>
                        {renderMessageContent(msg.content)}
                        {msg.secret && (
                          <span className="secret-inline-badge" data-testid="secret-inline-badge" aria-hidden>🔒</span>
                        )}
                      </div>
                      {msg.id && (
                        <SecretLockBadge
                          messageId={msg.id}
                          isSecret={!!msg.secret}
                          onChange={(next) => {
                            setMessages(prev => prev.map((m, i) => i === index ? { ...m, secret: next } : m));
                          }}
                        />
                      )}
                      {msg.pendingAction?.type === "post_tweet" && (
                        <TweetConfirmCard
                          pending={msg.pendingAction}
                          onSent={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "sent" } } : m))}
                          onCancelled={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "cancelled" } } : m))}
                        />
                      )}
                      {msg.pendingAction?.type === "send_text" && (
                        <SendTextConfirmCard
                          pending={msg.pendingAction}
                          onSent={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "sent" } } : m))}
                          onCancelled={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "cancelled" } } : m))}
                        />
                      )}
                      {msg.pendingAction?.type === "share_to_snap" && (
                        <SnapShareConfirmCard
                          pending={msg.pendingAction}
                          onSent={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "sent" } } : m))}
                          onCancelled={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "cancelled" } } : m))}
                        />
                      )}
                      {msg.pendingAction?.type === "share_to_tiktok" && (
                        <TikTokShareConfirmCard
                          pending={msg.pendingAction}
                          onSent={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "sent" } } : m))}
                          onCancelled={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "cancelled" } } : m))}
                        />
                      )}
                      {msg.pendingAction?.type === "share_to_instagram" && (
                        <InstagramShareConfirmCard
                          pending={msg.pendingAction}
                          onSent={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "sent" } } : m))}
                          onCancelled={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, pendingAction: { ...m.pendingAction, _resolved: "cancelled" } } : m))}
                        />
                      )}
                      {msg.crisisResources && <CrisisCard payload={msg.crisisResources} />}
                      {msg.personNudge && !msg._nudgeDismissed && (
                        <button
                          type="button"
                          className="person-nudge-pill"
                          data-testid="person-nudge-pill"
                          onClick={() => { setFocusPersonId(msg.personNudge.person_id); setShowPeople(true); }}
                        >
                          <span>💜 Tell me more about {msg.personNudge.person_name}</span>
                          <span
                            className="nudge-dismiss"
                            data-testid="person-nudge-dismiss"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMessages(prev => prev.map((m, i) => i === index ? { ...m, _nudgeDismissed: true } : m));
                              axios.post(`${API}/people/${msg.personNudge.person_id}/dismiss-nudge`).catch(() => {});
                            }}
                          >×</span>
                        </button>
                      )}
                      {msg.shareNudge && !msg._shareDismissed && (
                        <ShareNudgePill
                          nudge={msg.shareNudge}
                          messageContent={msg.content}
                          onDismissed={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, _shareDismissed: true } : m))}
                        />
                      )}
                      {msg.captureMoment && !msg._captureDismissed && (
                        <CaptureMomentPill
                          captureMoment={msg.captureMoment}
                          onCapture={() => { setMessages(prev => prev.map((m, i) => i === index ? { ...m, _captureDismissed: true } : m)); openCamera(msg.captureMoment.prompt || ""); }}
                          onUseWallpaper={() => {
                            const caption = (msg.content || "").slice(0, 200);
                            setMessages(prev => prev.map((m, i) => i === index ? {
                              ...m, _captureDismissed: true,
                              pendingAction: { type: "share_to_snap", caption_text: caption, wallpaper_id: wallpaperId, char_count: caption.length },
                            } : m));
                          }}
                          onDismissed={() => setMessages(prev => prev.map((m, i) => i === index ? { ...m, _captureDismissed: true } : m))}
                        />
                      )}
                    </React.Fragment>
                  ))}
                  {/* Phase 30.X.J — Newton's cradle "thinking" indicator.
                      Shows whenever isSending is true AND the last message in
                      the thread is the user's (so Aura hasn't replied yet, and
                      no optimistic _isAck bubble has taken over already). Just
                      three dots in a chat bubble — the outer two slide in
                      straight lines (left/right) while the middle stays put,
                      so it reads as "Newton's cradle energy transfer" without
                      any pendulum strings or bar above. Distinct from the
                      copyright 3-dot typing meme. */}
                  {isSending && messages.length > 0 && messages[messages.length - 1].role === "user" && (
                    <div
                      className="message assistant aura-thinking"
                      data-testid="aura-thinking-cradle"
                      role="status"
                      aria-label="Aura is writing"
                    >
                      {thinkingPhase > 0 && (
                        <span
                          key={thinkingPhase}
                          className="aura-thinking-whisper"
                          data-testid="aura-thinking-whisper"
                        >
                          {thinkingPhase === 1 ? "still thinking" : "this one’s a longer thought"}
                        </span>
                      )}
                      <div className="aura-cradle" aria-hidden="true">
                        <span className="cradle-dot cradle-dot-1" />
                        <span className="cradle-dot cradle-dot-2" />
                        <span className="cradle-dot cradle-dot-3" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              ) : composerExpanded ? (
                <div className="composer-empty-state" data-testid="composer-empty">
                  ✦ Say hi to your Aura
                </div>
              ) : null}
            </div>
            {/* Phase 30.B — draggable handle: pull up to expand, pull down to collapse */}
            <div
              className="composer-drag-handle"
              data-testid="composer-drag-handle"
              role="separator"
              aria-label="Drag to expand or collapse composer"
              onPointerDown={(e) => {
                dragStartYRef.current = e.clientY;
                e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (dragStartYRef.current == null) return;
                const dy = dragStartYRef.current - e.clientY;
                if (dy > 28 && !composerExpanded) {
                  setComposerExpanded(true);
                  dragStartYRef.current = e.clientY;
                } else if (dy < -28 && composerExpanded) {
                  setComposerExpanded(false);
                  dragStartYRef.current = e.clientY;
                }
              }}
              onPointerUp={() => { dragStartYRef.current = null; }}
              onPointerCancel={() => { dragStartYRef.current = null; }}
              onClick={() => setComposerExpanded((v) => !v)}
            />
            {showPhotoNudge && (
              <div className="photo-nudge" data-testid="photo-nudge">
                <span className="photo-nudge-icon">✨</span>
                <span className="photo-nudge-text">
                  want to send a photo with this?
                </span>
                <button
                  className="photo-nudge-yes"
                  onClick={() => {
                    const caption = inputMessage;
                    setInputMessage("");
                    setPhotoNudgeDismissed(false);
                    openCamera(caption);
                  }}
                  data-testid="photo-nudge-yes"
                >
                  Yes
                </button>
                <button
                  className="photo-nudge-no"
                  onClick={() => setPhotoNudgeDismissed(true)}
                  data-testid="photo-nudge-no"
                  aria-label="Not now"
                >
                  ×
                </button>
              </div>
            )}

            {/* Phase 30.X.G — staged wallpaper photo banner. When the user
                has picked a photo via the wallpaper-from-photo button, show
                a thumbnail + clear instructions so they know what to do next.
                Without this, the picker silently stages the file and the
                user thinks the button is broken. */}
            {stagedWallpaperPhoto && (
              <div className="staged-photo-banner" data-testid="staged-photo-banner">
                <img
                  src={URL.createObjectURL(stagedWallpaperPhoto)}
                  alt="staged"
                  className="staged-photo-thumb"
                />
                <div className="staged-photo-text">
                  <strong>Photo ready ✨</strong>
                  <span>Type the scene Aura should paint you into, then tap send.</span>
                </div>
                <button
                  className="staged-photo-remove"
                  onClick={() => setStagedWallpaperPhoto(null)}
                  data-testid="staged-photo-remove"
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </div>
            )}

            {/* Phase 30.C — Action row: wallpaper actions moved down from top bar.
                Outline icons w/ purple-pink stroke. Refresh is the primary CTA (gradient bg). */}
            <div className="composer-action-row" data-testid="composer-action-row">
              <button
                className="composer-action-btn is-primary"
                onClick={generateWallpaper}
                disabled={isGeneratingWallpaper || trialExpired}
                data-testid="refresh-wallpaper-btn"
                title={isGeneratingWallpaper ? "Creating new wallpaper…" : "Refresh wallpaper"}
                aria-label="Refresh wallpaper"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={isGeneratingWallpaper ? "is-spinning" : ""}>
                  <path d="M21 12a9 9 0 1 1-3.06-6.78L21 8" />
                  <polyline points="21 3 21 8 16 8" />
                  <path d="M3 12a9 9 0 0 0 14.94 6.78L21 16" opacity="0" />
                </svg>
              </button>
              <button
                className="composer-action-btn"
                onClick={async () => {
                  if (isGeneratingWallpaper || trialExpired) return;
                  try { await axios.post(`${API}/mood/random`); } catch (e) { /* non-fatal */ }
                  await generateWallpaper();
                }}
                disabled={isGeneratingWallpaper || trialExpired}
                title="Random mood + new wallpaper"
                data-testid="surprise-me-btn"
                aria-label="Roll random mood"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="3" />
                  <circle cx="8" cy="8" r="1.2" fill="currentColor" stroke="none" />
                  <circle cx="16" cy="8" r="1.2" fill="currentColor" stroke="none" />
                  <circle cx="8" cy="16" r="1.2" fill="currentColor" stroke="none" />
                  <circle cx="16" cy="16" r="1.2" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
                </svg>
              </button>
              <button
                className="composer-action-btn"
                onClick={() => wallpaper && downloadWallpaper(wallpaper, "aura-wallpaper.png")}
                disabled={!wallpaper}
                title="Save wallpaper to your phone"
                data-testid="save-wallpaper-btn"
                aria-label="Download wallpaper"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </button>
              <button
                className="composer-action-btn"
                onClick={() => setShowGallery(true)}
                title="My wallpapers"
                data-testid="gallery-btn"
                aria-label="Open wallpaper gallery"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="9" cy="9" r="2" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </button>
              <button
                className="composer-action-btn"
                onClick={() => wallpaper && setShowWallpaperShare(true)}
                disabled={!wallpaper}
                title="Share wallpaper"
                data-testid="composer-share-btn"
                aria-label="Share wallpaper"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              </button>

              {/* Phase 30.D — small settings ⋯ + notif 🔔 + streak 🔥 alongside share/send */}
              <button
                className={`composer-action-btn is-small${hasConnectedSocial === false ? " needs-social" : ""}`}
                onClick={() => setShowSettings(!showSettings)}
                data-testid="settings-btn"
                aria-label={hasConnectedSocial === false ? "Open settings — connect a social account" : "Open settings"}
                title="Settings"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
                  <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
                </svg>
                {hasConnectedSocial === false && (
                  <span className="settings-plus-badge" data-testid="settings-plus-badge" aria-hidden="true">+</span>
                )}
              </button>
              <button
                className="composer-action-btn is-small"
                onClick={() => { setShowNotifications(!showNotifications); if (unreadCount > 0) markNotificationsRead(); }}
                data-testid="notif-btn"
                aria-label="Notifications"
                title="Notifications"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                </svg>
                {unreadCount > 0 && <span className="notif-badge" data-testid="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
              </button>
              <div className="composer-action-btn is-small composer-streak-slot" data-testid="composer-streak-slot" aria-label="Streak">
                <StreakBadge
                  streak={streak.current}
                  longest={streak.longest}
                  grew={streak.grew}
                  atRisk={streak.atRisk}
                  onClick={() => {}}
                />
              </div>
            </div>

            {/* Phase 30.C — Textbox row: text input + mic outline in center + camera outline on right + send arrowhead */}
            <div className="composer-textbox-row" data-testid="composer-textbox-row">
              <div className="composer-textbox">
                <input
                  type="text"
                  className="chat-input"
                  placeholder={trialExpired ? "Subscribe to continue..." : isRecording ? "Recording..." : isVoiceProcessing ? "Aura is listening..." : "Message your Aura..."}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isSending || trialExpired || isRecording || isVoiceProcessing}
                  data-testid="chat-input"
                  ref={(el) => {
                    if (el && typeof window !== "undefined") {
                      try {
                        if (sessionStorage.getItem("aura_quick_launch") === "1") {
                          sessionStorage.removeItem("aura_quick_launch");
                          setTimeout(() => {
                            try { el.focus({ preventScroll: false }); el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
                          }, 200);
                        }
                      } catch (_) {}
                    }
                  }}
                />
                {/* Phase 30.C — Mic outline SILHOUETTE in the CENTER of the textbox with HOLD label */}
                <button
                  className={`composer-mic-silhouette ${isRecording ? 'recording' : ''}`}
                  onMouseDown={startMicRecording}
                  onMouseUp={stopMicAndSend}
                  onMouseLeave={isRecording ? cancelMicRecording : undefined}
                  onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); startMicRecording(); }}
                  onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); stopMicAndSend(); }}
                  onContextMenu={(e) => { e.preventDefault(); }}
                  disabled={isSending || trialExpired || isVoiceProcessing}
                  data-testid="chat-mic-btn"
                  aria-label="Hold to speak"
                  title={user?.has_premium ? "Hold to speak" : "Premium: hold to speak"}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#beta-gradient-stroke)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="2" width="6" height="11" rx="3" />
                    <path d="M5 11a7 7 0 0 0 14 0" />
                    <line x1="12" y1="18" x2="12" y2="22" />
                    <line x1="9" y1="22" x2="15" y2="22" />
                  </svg>
                  <span className="composer-hold-label" aria-hidden="true">HOLD</span>
                </button>
                {/* Phase 30.C — Camera outline SILHOUETTE all the way RIGHT inside textbox */}
                <button
                  className="composer-cam-silhouette"
                  onClick={() => openCamera("")}
                  disabled={isSending || trialExpired || isRecording || isVoiceProcessing}
                  data-testid="chat-camera-btn"
                  title={user?.has_premium ? "Show Aura" : "Premium: show Aura a photo"}
                  aria-label="Show Aura a photo"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="url(#beta-gradient-stroke)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8a2 2 0 0 1 2-2h2l2-3h6l2 3h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z" />
                    <circle cx="12" cy="13" r="3.5" />
                  </svg>
                </button>
                {/* Phase 30.X.G/H — hidden file input used by the
                    "Paint me into a wallpaper" option inside the
                    VisionSourcePicker (opened via the camera button above).
                    No separate composer button anymore — all photo options
                    live in one picker so users aren't confused. */}
                <input
                  ref={wallpaperPhotoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setStagedWallpaperPhoto(f);
                    e.target.value = "";
                  }}
                />
              </div>
              {/* Phase 30.C — Send: arrowhead ONLY (no tail), inside the grey box, elevated gradient */}
              <button
                className="composer-send-arrowhead"
                onClick={sendMessage}
                disabled={isSending || !inputMessage.trim() || trialExpired || isRecording || isVoiceProcessing}
                data-testid="send-btn"
                aria-label={isSending ? "Sending" : "Send"}
                title="Send"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {/* Age gate — shown ONCE per user after wallpaper setup, before they can use chat */}
      {user?.wallpaper_prompt && !user?.birthdate && !trialExpired && (
        <AgeGateModal
          userName={user.name}
          onComplete={() => refreshUser()}
        />
      )}

      {/* Parental controls setup — shown for under-18 users who set their age and haven't set up controls yet */}
      {user?.wallpaper_prompt && user?.birthdate && !trialExpired &&
        (user?.age_band === "child" || user?.age_band === "teen") &&
        !user?.parental_controls?.setup_completed && !showParentalSetup && user?.age_band === "child" && (
        <ParentalSetupModal
          user={user}
          onClose={() => {}}
          onComplete={() => refreshUser()}
        />
      )}

      {/* Manually openable parental setup modal */}
      {showParentalSetup && (
        <ParentalSetupModal
          user={user}
          onClose={() => setShowParentalSetup(false)}
          onComplete={() => { setShowParentalSetup(false); refreshUser(); }}
        />
      )}

      {/* PIN unlock modal — opened when a PIN-restricted feature is attempted */}
      {parentalUnlockFeature && (
        <ParentalUnlockModal
          feature={parentalUnlockFeature}
          onUnlocked={() => { setParentalUnlockFeature(null); refreshUser(); }}
          onCancel={() => setParentalUnlockFeature(null)}
        />
      )}

      {showPayment && (
        <PaymentModal
          tier={showPayment}
          user={user}
          discountPct={user?.base_discount_pct || 0}
          currentTier={user?.premium_tier || null}
          onClose={() => setShowPayment(null)}
          onSuccess={() => { setShowPayment(null); refreshUser(); }}
          onOpenPromo={() => { setShowPayment(null); setShowPromoRedeem(true); }}
        />
      )}

      {showSuccessPopup && (
        <PremiumSuccessPopup
          onClose={() => setShowSuccessPopup(false)}
          onUpgrade={() => { setShowSuccessPopup(false); setShowPayment('premium'); }}
        />
      )}

      {/* Trial Expired Lock */}
      {trialExpired && !showPayment && (
        <TrialExpiredOverlay
          onPay={() => setShowPayment('base')}
          onOpenPromo={() => setShowPromoRedeem(true)}
        />
      )}

      {/* Mood Journal */}
      {showMoodJournal && (
        <MoodJournalModal
          onClose={() => setShowMoodJournal(false)}
          isPremium={user?.has_premium}
          onUpgrade={() => { setShowMoodJournal(false); setShowPayment('premium'); }}
        />
      )}

      {/* Theme Picker — Phase 30.Q: themes are free for everyone. Hard-code
          isPremium so any lingering legacy paywall conditions inside the
          modal can never fire, and drop the onUpgrade hookup. */}
      {showThemePicker && (
        <ThemePickerModal
          onClose={() => setShowThemePicker(false)}
          isPremium={true}
          currentTheme={currentTheme}
          onThemeSelect={(theme) => { setCurrentTheme(theme); refreshUser(); }}
          onUpgrade={() => { setShowThemePicker(false); }}
          dynamicMood={Boolean(user?.dynamic_mood_theme)}
          onDynamicMoodChange={() => { refreshUser(); }}
        />
      )}

      {/* Phase 25.1 — Aura Pro waitlist teaser */}
      {showProTeaser && (
        <ProTeaserModal
          onClose={() => { setShowProTeaser(false); refreshUser(); }}
          alreadySignedUp={Boolean(user?.pro_interest)}
        />
      )}

      {/* Phase 25.4 — Snap + Spotify beta access request */}
      {showBetaAccess && (
        <BetaAccessModal onClose={() => setShowBetaAccess(false)} />
      )}

      {/* Aura Stories */}
      {showStories && (
        <StoriesFeedModal
          onClose={() => setShowStories(false)}
        />
      )}

      {/* Notifications */}
      {showNotifications && (
        <NotificationsModal
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
          formatTime={formatNotifTime}
          onTrialNudgeTap={() => { setShowNotifications(false); setShowPayment('base'); }}
        />
      )}

      {/* Voice Picker */}
      {showVoicePicker && (
        <VoicePickerModal
          onClose={() => setShowVoicePicker(false)}
          isPremium={user?.has_premium}
          currentVoice={preferredVoice}
          onVoiceSelect={(v) => { setPreferredVoice(v); refreshUser(); }}
          onUpgrade={() => { setShowVoicePicker(false); setShowPayment('premium'); }}
        />
      )}

      {/* Voice Call (full screen) */}
      {showVoiceCall && (
        <VoiceCallModal
          onClose={() => setShowVoiceCall(false)}
          voice={preferredVoice}
          wallpaper={wallpaper}
          mood={mood}
          onMoodUpdate={(m) => setMood({ ...mood, ...m })}
          onWallpaperRefresh={() => generateWallpaper(true)}
        />
      )}

      {/* Vision source picker (camera vs library vs paint-me-in vs AR) */}
      {showVisionSourcePicker && (
        <VisionSourcePicker
          onCamera={() => { setShowVisionSourcePicker(false); setShowCamera(true); }}
          onLibrary={() => { setShowVisionSourcePicker(false); setShowPhotoPicker(true); }}
          /* Phase 30.X.H — Aurora+ "Paint me into a wallpaper" lives inside
             the same picker so users see ALL their photo options in one
             place. Tapping this stages a photo file for the chat composer,
             and the user types a scene + sends. Server-side aurora gating
             still enforces premium tier. */
          onWallpaperPhoto={() => {
            if (!user?.has_premium) {
              setShowVisionSourcePicker(false);
              setShowPayment('premium');
              return;
            }
            setShowVisionSourcePicker(false);
            wallpaperPhotoInputRef.current?.click();
          }}
          wallpaperPhotoLocked={!user?.has_premium}
          /* Phase 29.I — Surface Aura AR lenses through the camera button
             when (a) Camera Kit is configured server-side AND (b) the user
             has Snapchat connected. The modal itself handles tier-locked
             users with an upsell, so we still render the option (with a
             lock badge) when configured but not eligible — that's the same
             discoverability pattern the top-bar ✨ button uses. */
          onAR={(arStatus?.configured && snapConnected)
            ? () => { setShowVisionSourcePicker(false); setShowAR(true); }
            : undefined}
          arLocked={!!(arStatus?.configured && snapConnected && !arStatus?.eligible)}
          onCancel={() => setShowVisionSourcePicker(false)}
        />
      )}

      {/* Camera (Vision — take new photo) */}
      {showCamera && (
        <CameraCaptureModal
          onClose={() => setShowCamera(false)}
          onCaptured={handleCameraCaptured}
          initialContext={cameraContext}
          onMoodUpdate={(m) => setMood({ ...mood, ...m })}
          /* Phase 30.X.H — Aurora+ "Paint into wallpaper" lives right in the
             camera review step so the snap → wallpaper path is one click,
             no "million questions" chat flow. */
          onPaintWallpaper={({ file, locked }) => {
            if (locked) {
              setShowCamera(false);
              setShowPayment('premium');
              return;
            }
            if (file) {
              setStagedWallpaperPhoto(file);
              setShowCamera(false);
            }
          }}
          paintWallpaperLocked={!user?.has_premium}
        />
      )}

      {/* Photo Picker (Vision — pick from library) */}
      {showPhotoPicker && (
        <PhotoPickerModal
          onClose={() => setShowPhotoPicker(false)}
          onCaptured={handleCameraCaptured}
          initialContext={cameraContext}
          onMoodUpdate={(m) => setMood({ ...mood, ...m })}
          onPaintWallpaper={({ file, locked }) => {
            if (locked) {
              setShowPhotoPicker(false);
              setShowPayment('premium');
              return;
            }
            if (file) {
              setStagedWallpaperPhoto(file);
              setShowPhotoPicker(false);
            }
          }}
          paintWallpaperLocked={!user?.has_premium}
        />
      )}

      {/* People */}
      {showPeople && (
        <PeopleModal
          onClose={() => { setShowPeople(false); setFocusPersonId(null); }}
          focusPersonId={focusPersonId}
        />
      )}

      {/* Connected Accounts */}
      {showIntegrations && (
        <IntegrationsModal
          onClose={() => setShowIntegrations(false)}
          isPremium={user?.has_premium}
          onUpgrade={() => { setShowIntegrations(false); setShowPayment('premium'); }}
        />
      )}

      {/* Promo: user redemption */}
      {showPromoRedeem && (
        <PromoRedeemModal
          onClose={() => {
            setShowPromoRedeem(false);
            // Clear pending ref so closing doesn't keep re-opening it
            if (pendingRefCode) {
              localStorage.removeItem("aura_pending_ref");
              setPendingRefCode("");
            }
          }}
          initialCode={pendingRefCode}
          onRedeemed={async () => {
            localStorage.removeItem("aura_pending_ref");
            setPendingRefCode("");
            await refreshUser();
          }}
        />
      )}

      {/* Promo: admin management */}
      {showAdminPromos && user?.is_admin && (
        <AdminPromosModal onClose={() => setShowAdminPromos(false)} />
      )}
      {showOutboundCalls && (
        <OutboundCallsModal onClose={() => setShowOutboundCalls(false)} user={user} />
      )}
      {showCreatorStudio && (
        <CreatorStudioModal onClose={() => setShowCreatorStudio(false)} user={user} />
      )}
      {showAdminEmailHealth && user?.is_admin && (
        <AdminEmailHealthModal onClose={() => setShowAdminEmailHealth(false)} />
      )}
      {showAdminBillingPortal && user?.is_admin && (
        <AdminBillingPortalModal onClose={() => setShowAdminBillingPortal(false)} />
      )}

      {/* Wallpaper Gallery */}
      {showGallery && (
        <WallpaperGalleryModal
          onClose={() => setShowGallery(false)}          onSetActive={(img) => setWallpaper(img)}
        />
      )}

      {/* Phase 22 — Snap Camera Kit AR Lenses */}
      {showAR && (
        <ARCameraModal
          onClose={() => setShowAR(false)}
          onSnapSaved={() => {
            // Tell gallery to refresh next time it opens
            try { window.dispatchEvent(new CustomEvent("aura:wallpaper-saved")); } catch (_) {}
          }}
        />
      )}

      {/* Referrals */}
      {showReferral && (
        <ReferralModal
          onClose={() => setShowReferral(false)}
          onUpgrade={() => { setShowReferral(false); setShowPayment('base'); }}
        />
      )}

      {/* Soft social-connect prompt — one-time, new premium users only */}
      {showSocialPrompt && (
        <SocialConnectPrompt
          onConnect={() => {
            setShowSocialPrompt(false);
            setSocialPromptHandled(true);
            setShowIntegrations(true);
          }}
          onDismiss={() => {
            setShowSocialPrompt(false);
            setSocialPromptHandled(true);
          }}
        />
      )}

      {/* Account deletion */}
      {showDeleteAccount && (
        <DeleteAccountModal
          onClose={() => setShowDeleteAccount(false)}
          onDeleted={() => { setShowDeleteAccount(false); logout(); }}
        />
      )}

      {/* Phase 30.B — In-chat Wallpaper share sheet (3-dot floating over wallpaper) */}
      {showWallpaperShare && wallpaper && (
        <PhotoShareSheet
          imageBase64={wallpaper}
          wallpaperId={wallpaperId}
          title="Share this wallpaper"
          defaultCaption=""
          onClose={() => setShowWallpaperShare(false)}
          onComplete={() => setShowWallpaperShare(false)}
        />
      )}
    </div>
  );
}
